import axios from "axios";

import Response from "./response";
import { SERVICE_URL } from "./app.const";

const avatarSourceCache = new Map();

const authorizationHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const dataUrlToBlob = (dataUrl) => {
  const [metadata, encodedData] = dataUrl.split(",");
  const contentType = metadata.match(/^data:([^;]+);base64$/)?.[1];
  if (!contentType || !encodedData) throw new Error("Invalid image data");

  const bytes = Uint8Array.from(atob(encodedData), (character) => character.charCodeAt(0));
  return new Blob([bytes], { type: contentType });
};

const clearCachedCharacterAvatar = (characterId) => {
  const pathPrefix = `/characters/${characterId}/avatar`;
  [...avatarSourceCache.entries()].forEach(([key, sourcePromise]) => {
    if (!key.startsWith(pathPrefix)) return;
    sourcePromise.then((source) => URL.revokeObjectURL(source)).catch(() => {});
    avatarSourceCache.delete(key);
  });
};

export async function uploadCharacterAvatar(characterId, dataUrl, token) {
  const response = new Response();
  try {
    const blob = dataUrlToBlob(dataUrl);
    const formData = new FormData();
    const extension = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" }[blob.type] || "img";
    formData.append("file", blob, `character-avatar.${extension}`);
    const result = await axios.put(
      `${SERVICE_URL}/characters/${characterId}/avatar`,
      formData,
      { headers: authorizationHeaders(token) }
    );
    clearCachedCharacterAvatar(characterId);
    response.handleResult(result);
  } catch (error) {
    response.handleError(error);
  }
  return response;
}

export async function updateCharacterAvatarUrl(characterId, avatarUrl, token) {
  const response = new Response();
  try {
    const result = await axios.put(
      `${SERVICE_URL}/characters/${characterId}/avatar-url`,
      { avatarUrl },
      { headers: { "Content-Type": "application/json", ...authorizationHeaders(token) } }
    );
    clearCachedCharacterAvatar(characterId);
    response.handleResult(result);
  } catch (error) {
    response.handleError(error);
  }
  return response;
}

export async function deleteCharacterAvatar(characterId, token) {
  const response = new Response();
  try {
    const result = await axios.delete(
      `${SERVICE_URL}/characters/${characterId}/avatar`,
      { headers: authorizationHeaders(token) }
    );
    clearCachedCharacterAvatar(characterId);
    response.handleResult(result);
  } catch (error) {
    response.handleError(error);
  }
  return response;
}

export function loadCharacterAvatarSource(avatarUrl, token) {
  if (!avatarUrl || !avatarUrl.startsWith("/characters/")) {
    return Promise.resolve(avatarUrl || "");
  }

  if (!avatarSourceCache.has(avatarUrl)) {
    const sourceRequest = axios.get(`${SERVICE_URL}${avatarUrl}`, {
        headers: authorizationHeaders(token),
        responseType: "blob",
      })
      .then((response) => URL.createObjectURL(response.data))
      .catch((error) => {
        avatarSourceCache.delete(avatarUrl);
        throw error;
      });
    avatarSourceCache.set(avatarUrl, sourceRequest);
  }
  return avatarSourceCache.get(avatarUrl);
}

export function resetCharacterAvatarCache() {
  [...avatarSourceCache.values()].forEach((sourcePromise) => {
    sourcePromise.then((source) => URL.revokeObjectURL(source)).catch(() => {});
  });
  avatarSourceCache.clear();
}
